import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type Mensagem = Database['public']['Tables']['mensagens']['Row'];

export interface ConversaConsumer {
  cliente_id: string;
  empresa: { id: string; nome: string; logo_url: string | null };
  ultima_mensagem: string | null;
  ultima_em: string | null;
  nao_lidas: number;
}

export interface EnviarMensagemAppInput {
  clienteId: string;
  conteudo: string;
  tipoMedia?: 'image' | 'audio';
  mediaStoragePath?: string;
  mediaMimeType?: string;
}

const BUCKET = 'app-chat-media';

/** Find-or-create da conversa do consumer com uma empresa (RPC). Devolve cliente_potencial_id. */
export async function iniciarConversaApp(orgId: string): Promise<string> {
  const { data, error } = await supabase.rpc('iniciar_conversa_app', { p_org_id: orgId });
  if (error) throw error;
  return data as string;
}

/** Consumer envia mensagem (texto e/ou media já carregada). */
export async function enviarMensagemApp(input: EnviarMensagemAppInput): Promise<string> {
  const { data, error } = await supabase.rpc('enviar_mensagem_app', {
    p_cliente_id: input.clienteId,
    p_conteudo: input.conteudo,
    p_tipo_media: input.tipoMedia ?? undefined,
    p_media_storage_path: input.mediaStoragePath ?? undefined,
    p_media_mime_type: input.mediaMimeType ?? undefined,
  });
  if (error) throw error;
  return data as string;
}

/** Lista as conversas do consumer (RPC, com nome/logo da empresa). */
export async function listarConversasConsumer(): Promise<ConversaConsumer[]> {
  const { data, error } = await supabase.rpc('listar_conversas_consumer');
  if (error) throw error;
  return (data as unknown as ConversaConsumer[]) ?? [];
}

/** Consumer marca as mensagens da empresa como vistas. */
export async function marcarLidasConsumer(clienteId: string): Promise<void> {
  const { error } = await supabase.rpc('marcar_lidas_consumer', { p_cliente_id: clienteId });
  if (error) throw error;
}

/** Mensagens de uma conversa (RLS trata o acesso). */
export async function listarMensagensConversa(clienteId: string): Promise<Mensagem[]> {
  const { data, error } = await supabase
    .from('mensagens').select('*')
    .eq('cliente_potencial_id', clienteId)
    .order('criado_em', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export interface EnviarEmpresaAppInput {
  clienteId: string;
  orgId: string;
  conteudo: string;
  autorId: string;
  nomeRemetente: string;
  tipoMedia?: 'image' | 'audio';
  mediaStoragePath?: string;
  mediaMimeType?: string;
}

/** Empresa responde a um lead app (insert direto via RLS de org; NÃO toca no WhatsApp). */
export async function enviarMensagemEmpresaApp(input: EnviarEmpresaAppInput): Promise<Mensagem> {
  const { data, error } = await supabase.from('mensagens').insert({
    cliente_potencial_id: input.clienteId,
    organization_id: input.orgId,
    conteudo: input.conteudo,
    canal: 'app',
    remetente: 'atendente',
    autor_id: input.autorId,
    nome_remetente: input.nomeRemetente,
    lida: true,
    lida_consumer: false,
    tipo_media: input.tipoMedia ?? null,
    media_storage_path: input.mediaStoragePath ?? null,
    media_mime_type: input.mediaMimeType ?? null,
    media_status: input.mediaStoragePath ? 'ready' : null,
  }).select().single();
  if (error) throw error;
  return data;
}

/** Upload de media para o bucket app-chat-media em {clienteId}/{uuid}.{ext}; devolve o storage path. */
export async function uploadChatAppMedia(clienteId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const path = `${clienteId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type });
  if (error) throw error;
  return path;
}

/** Signed URL (1h) para um ficheiro do bucket privado. */
export async function getChatAppSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}
